import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SpinReward, User, SpinResult, SpinHistory } from '../types';
import { spinWithReward } from '../services/api';
import { colors } from '../theme/themeConfig';
import PageSwitcher from '../components/PageSwitcher';
import { useRewardReel } from '../hooks/useRewardReel';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
// Import contexts and components
import { useUser } from '../contexts/UserContext';
import { useSpinRewards } from '../contexts/SpinRewardsContext';
import PageHeader from '../components/PageHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const RP_COLORS = {
  main: '#FEEF00',
  shadow: '#FC4700',
}
// linear-gradient(90deg, #FC4700 0%, #FEEF00 45%, #FC4700 100%);
const UC_COLORS = {
  main: '#FFFFFF',
  shadow: '#D7D7D7',
}
// linear-gradient(90deg, #D7D7D7 0%, #FFFFFF 45%, #D7D7D7 100%);
const FONT_COLORS = {
  main: '#FFFFFF',
  shadow: '#D7D7D7',
}
// linear-gradient(90deg, #D7D7D7 0%, #FFFFFF 45%, #D7D7D7 100%);

const SPIN_COST = 150;

const NewPaidRoulettePage: React.FC = () => {
  // const ITEM_WIDTH = 130; // item width + margin/padding
  const ITEM_WIDTH = 170; // item width + margin/padding
  
  // Use contexts instead of fetching data directly
  const { user, telegramUser, isLoading: userLoading, refreshUser, setUser } = useUser();
  const { paidRewards, isLoading: rewardsLoading } = useSpinRewards();
  
  // State for spin result and popup
  const [spinResult, setSpinResult] = useState<SpinResult | null>(null);
  const [spinHistory, setSpinHistory] = useState<SpinHistory | null>(null);
  const [showResultPopup, setShowResultPopup] = useState(false);
  
  // Combine loading states with timeout to prevent infinite loading
  const [loading, setLoading] = useState(true);
  
  // Track when loading started
  const [loadStartTime] = useState(Date.now());
  
  // Snackbar state for notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('info');
  
  useEffect(() => {
    
    // Update loading state based on context loading states
    setLoading(userLoading || rewardsLoading);
    
    // Add a timeout to prevent infinite loading
    const timer = setTimeout(() => {
      if (loading && (Date.now() - loadStartTime) > 5000) {
        setLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [userLoading, rewardsLoading, loading, loadStartTime]);
  
  // Add a shuffle trigger state to force reshuffling after spin completion
  const [shuffleTrigger, setShuffleTrigger] = useState(0);
  
  // Use paidRewards from context instead of local state, shuffled on each render
  const items = React.useMemo(() => {
    // Create a copy of the array to avoid mutating the original
    const shuffledRewards = [...(paidRewards || [])];
    
    // Fisher-Yates shuffle algorithm
    for (let i = shuffledRewards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledRewards[i], shuffledRewards[j]] = [shuffledRewards[j], shuffledRewards[i]];
    }
    
    return shuffledRewards;
  }, [paidRewards, shuffleTrigger]);
  
  
  // Function to handle spin completion
  const handleSpinComplete = async (reward: SpinReward) => {
    
    if (telegramUser) {
      try {
        // Save spin history to backend
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        try {
          // Make the API call with a signal to allow timeout cancellation
          const response = await spinWithReward();
          
          if (response && response.spinHistory) {
            setSpinHistory(response.spinHistory);
            
            // Refresh user data to update the balance
            // await refreshUser();
            setUser({
              ...user!,
              bonusBalance: response.newBonusBalance,
            })
            
            // Trigger a reshuffle of the rewards
            setShuffleTrigger(prev => prev + 1);
            
            // Create result object for popup
            const result: SpinResult = {
              success: true,
              message: 'Поздравляем!',
              reward: {
                type: reward.rewardType,
                amount: reward.rewardAmount,
                minPurchase: reward.minPurchase
              }
            };
            
            // Show result popup
            setSpinResult(result);
            setShowResultPopup(true); // Explicitly set popup to show in success case
            
            
            // Refresh user data to update bonus balance
            // if (response.newBonusBalance !== undefined) {
            //   await refreshUser();
            // }
          } else {
            console.error('Invalid response format:', response);
            throw new Error('Invalid response format');
          }
        } catch (error) {
          console.error('Error in API request:', error);
          
          // Even if the API request fails, still show a result to the user
          // This ensures they see something after spinning
          const result: SpinResult = {
            success: true, // Still show as success to the user
            message: 'Поздравляем!',
            reward: {
              type: reward.rewardType,
              amount: reward.rewardAmount,
              minPurchase: reward.minPurchase
            }
          };
          
          setSpinResult(result);
          setShowResultPopup(true);
        } finally {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        console.error('Error recording spin:', error);
      }
    }
  };
  
  // Use our custom hook to handle all the spinning logic
  const { 
    reelRef, 
    isSpinning, 
    finalItemIndex, 
    selectedReward, 
    startSpinning: hookStartSpinning 
  } = useRewardReel({
    items,
    itemWidth: ITEM_WIDTH,
    duration: 8000,
    minRotations: 10,
    onSpinComplete: handleSpinComplete
  });
  
  // Custom startSpinning function with balance check
  const startSpinning = async () => {
    if (isSpinning) return;
    
    // Check if user has enough balance
    if (!hasEnoughBalance) {
      // Show notification about insufficient balance
      setSnackbarMessage(`Недостаточно баланса. Требуется: ${MINIMUM_BALANCE_REQUIRED} рефбаллов`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    setShowResultPopup(false);
    await hookStartSpinning();
  };
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 375px)');

  // Define the minimum balance required to spin
  const MINIMUM_BALANCE_REQUIRED = SPIN_COST; // 300 refpoints
  
  // Check if user has enough balance to spin
  const hasEnoughBalance = user && user.bonusBalance >= MINIMUM_BALANCE_REQUIRED;
  
  // Create action button for PageHeader - button to navigate to bonus page
  const actionButton = (
    <IconButton
      onClick={() => navigate('/bonus')}
      sx={{
        color: colors.text.primary,
        backgroundColor: colors.primary.main,
        '&:hover': {
          backgroundColor: colors.primary.dark,
        }
      }}
    >
      <AddCircleOutlineOutlinedIcon />
    </IconButton>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        {/* Page Switcher Component */}
        <PageSwitcher
          pages={[
            { path: '/paid-roulette', label: 'Рулетка' },
            { path: '/free-wheel', label: 'Бонус' },
          ]}
        />
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Render the spin result popup
  const renderSpinResult = () => {
    
    if (!showResultPopup || !spinResult || !spinResult.success || !spinResult.reward) {
      return null;
    }
    
    const reward = spinResult.reward;
    
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 34, 49, 0.85)', // Darker, more opaque background
          backdropFilter: 'blur(8px)', // Blur effect for the background
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px',
        }}
        onClick={() => setShowResultPopup(false)} // Close popup on click
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '500px',
            textAlign: 'center',
            animation: 'fadeIn 0.5s ease-out',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(20px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            },
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFD700', // Gold color for celebration
              fontWeight: 'bold',
              marginBottom: '24px',
              textShadow: '0 2px 4px rgba(0,0,0,0.4)',
            }}
          >
            Юху!
          </Typography>
          
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFFFFF',
              fontWeight: 'bold',
              marginBottom: '16px',
              lineHeight: 1.4,
              textShadow: '0 2px 4px rgba(0,0,0,0.4)',
            }}
          >
            Вы выиграли{' '}
            <Box component="span" sx={{ color: colors.primary.main, fontSize: '120%' }}>
              {reward.type === 'uc' 
                ? `${reward.amount} UC` 
                : `${reward.amount} рефбаллов`}
            </Box>
          </Typography>


          {reward.type === 'rp' && (
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#FFFFFF',
                fontWeight: 'bold',
                marginBottom: '16px',
                lineHeight: 1.4,
                textShadow: '0 2px 4px rgba(0,0,0,0.4)',
              }}
            >
              Они были начислены на ваш бонусный баланс
            </Typography>
          )}
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#FFFFFF',
              marginTop: '40px',
              opacity: 0.7,
              fontSize: '14px',
            }}
          >
            Нажмите в любом месте, чтобы продолжить
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        maxHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto', 
        paddingBottom: '80px',
        position: 'relative', // Added for absolute positioning of children
        padding: theme.spacing(2),
        paddingTop: 0,
      }}
    > 
      {/* Page Switcher Component - now sticky at top */}
      <PageSwitcher
        pages={[
          { path: '/paid-roulette', label: 'Рулетка' },
          { path: '/free-wheel', label: 'Бонус' },
        ]}
      />
      
      
        {/* PageHeader Component with action button */}
        <PageHeader
          actionButton={actionButton}
        />
      

        <div style={{
          position: 'relative',
          marginTop: '30%',
          width: '100%',
          height: '150px',
          backgroundColor: '#132F3E',
          borderRadius: '8px',
          border: '4px solid #0D1B24',
          overflow: 'hidden',
          marginBottom: '20px',
        }}>
        {/* Reel items container */}
        <div
          ref={reelRef}
          style={{
            display: 'flex',
            padding: '10px',
            gap: '10px',
            height: '100%',
            willChange: 'scroll-position',
            overflowX: 'hidden',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE
          }}
        >
          {/* Hide scrollbar for Chrome/Safari */}
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {/* Custom reward cards with background images */}
          {[...items, ...items, ...items].map((item, index) => (
            <div
              key={`custom-${index}`}
              data-reward-id={item.id}
              style={{
                width: '160px',
                height: '120px', // 160:120 aspect ratio
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                // boxShadow: finalItemIndex !== null && items[finalItemIndex]?.id === item.id
                //   ? '0 0 15px 5px #FFD700' // Gold glow for selected item
                //   : '0 4px 8px rgba(0,0,0,0.3)',
                border: `2px solid #0D1B24`,
                // border: finalItemIndex !== null && items[finalItemIndex]?.id === item.id
                //   ? '2px solid #FFD700' // Gold border for selected item
                //   : `2px solid #0D1B24`,
                position: 'relative',
                overflow: 'hidden',
                transform: finalItemIndex !== null && items[finalItemIndex]?.id === item.id
                  ? 'scale(1.05)' // Slightly larger for selected item
                  : 'scale(1)',
                transition: 'transform 0.3s, box-shadow 0.3s, border 0.3s',
                backgroundImage: `url(${item.rewardType === 'uc' ? '/uc-reward-mini.png' : '/rp-reward-mini.png'})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Reward type in upper left corner */}
              <div
                style={{
                  position: 'absolute',
                  top: '5px',
                  left: '5px',
                  fontFamily: 'Unbounded, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '14px', // Adjusted for 160:120 aspect ratio
                  background: `linear-gradient(90deg, ${FONT_COLORS.shadow} 0%, ${FONT_COLORS.main} 45%, ${FONT_COLORS.shadow} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                  // textShadow: `0 1px 2px rgba(0, 0, 0, 0.5)`,
                  padding: '2px',
                }}
              >
                {item.rewardType === 'uc' ? 'UC' : 'РЕФБАЛЛОВ'}
              </div>
              
              {/* Reward amount centered in card */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 100%)',
                }}
              >
                <div
                  style={{
                    fontFamily: 'Unbounded, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '48px', // Adjusted for 160:120 aspect ratio
                    background: `linear-gradient(90deg, ${FONT_COLORS.shadow} 0%, ${FONT_COLORS.main} 45%, ${FONT_COLORS.shadow} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    color: 'transparent',
                    // textShadow: `0 2px 4px rgba(0, 0, 0, 0.5)`,
                    lineHeight: '1',
                    textAlign: 'center',
                  }}
                >
                  {item.rewardAmount}
                </div>
              </div>
            </div>
          ))}
        </div>

        
        {/* Center marker */}
        <div style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: '50%',
          width: '4px',
          backgroundColor: '#FFD700',
          transform: 'translateX(-50%)',
          zIndex: 3,
          boxShadow: '0 0 10px 2px rgba(255, 215, 0, 0.5)',
        }} />
      </div>

      {/* Fixed button container at the bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          backgroundColor: 'rgba(19, 47, 62, 0.9)', // Slightly transparent background
          backdropFilter: 'blur(8px)',
          borderTop: `1px solid ${colors.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          color={hasEnoughBalance ? "primary" : "error"}
          size="large"
          onClick={startSpinning}
          disabled={isSpinning || !hasEnoughBalance}
          sx={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: hasEnoughBalance ? colors.primary.dark : colors.error.main,
            },
            position: 'relative',
            overflow: 'hidden',
            ...(hasEnoughBalance ? {} : {
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.1) 10px, transparent 10px, transparent 20px)',
                animation: 'moveStripes 3s linear infinite',
              },
              '@keyframes moveStripes': {
                '0%': { backgroundPosition: '0 0' },
                '100%': { backgroundPosition: '28px 28px' }
              }
            })
          }}
        >
          {isSpinning ? 'Вжуххх...' : hasEnoughBalance ? 'Крутить рулетку' : `Недостаточно баланса (${MINIMUM_BALANCE_REQUIRED} рефбаллов)`}
        </Button>
        
        {/* Popup is now rendered at the end of the component */}
        
        <Button
          variant="outlined"
          onClick={() => navigate('/spin-history')}
          sx={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            textTransform: 'none',
            borderColor: colors.primary.main,
            color: colors.primary.main,
            '&:hover': {
              borderColor: colors.primary.dark,
            }
          }}
        >
          Мои призы
        </Button>
      </Box>
      
      {/* Render the spin result popup when available */}
      {renderSpinResult()}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewPaidRoulettePage;
